/* FM Product Compare — bar + comparison table controller.
 * 06-Jun-2026. Paired with sections/fm-compare-bar.liquid and
 * templates/product.compare.liquid.
 *
 * Selection is in-page only (no persistence) for the MVP. Checkboxes
 * live in the product grid, which the facet flow re-renders wholesale,
 * so we (a) listen for `change` via delegation and (b) re-apply checked
 * state after each grid swap via a MutationObserver.
 */
(function () {
  'use strict';

  var root = document.getElementById('fm-compare');
  if (!root) return;

  var MIN = parseInt(root.getAttribute('data-min'), 10) || 2;
  var MAX = parseInt(root.getAttribute('data-max'), 10) || 4;
  var NOUN = root.getAttribute('data-noun') || 'Products';
  var NOUN_LC = NOUN.toLowerCase();

  /* ——— Presentation map — labels + grouping ———
   * Edit freely. `LABELS` overrides the auto-humanised label for a key.
   * `GROUPS` is checked in order (first match wins); any key that matches
   * none falls into the final catch-all. Tests run against the lowercased
   * metafield key. `tagline` is special-cased as the headline "Best for"
   * row and is never grouped. A group header only renders if it has rows. */
  var LABELS = {
    tagline: 'Best for'
  };
  var GROUPS = [
    { title: 'Ratings',     test: function (k) { return /rating/.test(k); } },
    { title: 'Environment', keys: ['suitable_for_wet_areas'], test: function (k) { return /resist/.test(k); } },
    { title: 'Safety',      test: function (k) { return /safety|fire_test|bevel|hazard/.test(k); } },
    { title: 'Dimensions',  keys: ['made_to_measure', 'mat_format'], test: function (k) { return /weight|thick|size|length|width|height|gauge|diameter|dimension/.test(k); } },
    { title: 'Additional features', test: function () { return true; } }
  ];

  var introCount = root.querySelector('[data-fm-compare-introcount]');
  var itemsEl = root.querySelector('[data-fm-compare-items]');
  var openBtn = root.querySelector('[data-fm-compare-open]');
  var btnLabel = root.querySelector('[data-fm-compare-btnlabel]');
  var toggle = root.querySelector('[data-fm-compare-toggle]');
  var panel = root.querySelector('.fm-compare__panel');
  var panelTitle = root.querySelector('[data-fm-compare-paneltitle]');
  var closeBtn = root.querySelector('[data-fm-compare-close]');
  var tableEl = root.querySelector('[data-fm-compare-table]');
  var chipTpl = document.getElementById('fm-compare-chip');

  var selected = new Map();   // id -> { id, handle, title, image, url }
  var cache = new Map();      // handle -> compare JSON
  var expanded = false;

  function track(event, props) {
    try { if (window.posthog && window.posthog.capture) window.posthog.capture(event, props || {}); } catch (e) {}
  }

  function cardData(input) {
    return {
      id: input.getAttribute('data-fm-compare-product'),
      handle: input.getAttribute('data-fm-compare-handle'),
      title: input.getAttribute('data-fm-compare-title') || '',
      image: input.getAttribute('data-fm-compare-image') || '',
      url: input.getAttribute('data-fm-compare-url') || ''
    };
  }

  /* ——— Selection ——— */
  document.addEventListener('change', function (e) {
    var input = e.target.closest && e.target.closest('[data-fm-compare-product]');
    if (!input) return;
    var id = input.getAttribute('data-fm-compare-product');
    if (input.checked) {
      if (selected.size >= MAX) {
        input.checked = false;
        pulseLimit();
        return;
      }
      var data = cardData(input);
      selected.set(id, data);
      track('compare_product_added', { product_id: id, handle: data.handle, count: selected.size });
    } else {
      selected.delete(id);
    }
    syncCheckboxes();
    render();
  });

  // Remove a product from its chip.
  itemsEl.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.fm-compare__chip-remove');
    if (!btn) return;
    var chip = btn.closest('[data-fm-compare-chip]');
    if (!chip) return;
    selected.delete(chip.getAttribute('data-id'));
    syncCheckboxes();
    render();
  });

  /* ——— Expand / collapse ——— */
  openBtn.addEventListener('click', function () { if (selected.size >= MIN) openPanel(); });
  toggle.addEventListener('click', function () { expanded ? closePanel() : openPanel(); });
  closeBtn.addEventListener('click', closePanel);

  function openPanel() {
    expanded = true;
    panel.hidden = false;
    root.classList.add('is-expanded');
    toggle.setAttribute('aria-expanded', 'true');
    track('compare_opened', { count: selected.size, handles: handles() });
    ensureData().then(buildTable);
  }
  function closePanel() {
    expanded = false;
    panel.hidden = true;
    root.classList.remove('is-expanded');
    toggle.setAttribute('aria-expanded', 'false');
  }

  function handles() {
    var out = [];
    selected.forEach(function (p) { out.push(p.handle); });
    return out;
  }

  /* ——— Data fetch (server-rendered ?view=compare) ——— */
  function ensureData() {
    var jobs = [];
    selected.forEach(function (p) {
      if (cache.has(p.handle)) return;
      var url = p.url.split('?')[0] + '?view=compare';
      jobs.push(
        fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (d) { cache.set(p.handle, d); })
          .catch(function () { cache.set(p.handle, { fields: [], error: true }); })
      );
    });
    return Promise.all(jobs);
  }

  /* ——— Render the bar ——— */
  function render() {
    var n = selected.size;
    root.hidden = n === 0;
    if (introCount) introCount.textContent = n + ' ' + NOUN_LC + ' selected';
    if (btnLabel) btnLabel.textContent = 'Compare ' + n + ' ' + NOUN;
    openBtn.disabled = n < MIN;

    itemsEl.innerHTML = '';
    selected.forEach(function (p) { itemsEl.appendChild(chip(p)); });

    if (n < MIN && expanded) closePanel();
    if (expanded) ensureData().then(buildTable);
  }

  function chip(p) {
    var node = chipTpl.content.firstElementChild.cloneNode(true);
    node.setAttribute('data-id', p.id);
    var img = node.querySelector('.fm-compare__chip-img');
    if (p.image) { img.src = p.image; img.alt = p.title; } else { img.remove(); }
    node.querySelector('.fm-compare__chip-name').textContent = p.title;
    return node;
  }

  /* ——— Render the comparison table ——— */
  function cell(cls, text) {
    var d = document.createElement('div');
    d.className = 'fm-compare__cell ' + (cls || '');
    if (text != null) d.textContent = text;
    return d;
  }

  /* Rating fields render as a row of circles (0–5, like review stars)
   * instead of text. A value only becomes circles when it's numeric and
   * within 0–5; non-numeric rating values (e.g. legacy "Good"/"Better"/
   * "Best") fall back to plain text until the metafield is rescored, so
   * mixed states render gracefully. */
  var RATING_MAX = 5;
  function parseRating(v) {
    if (v == null) return null;
    var s = String(v).trim();
    if (!/^\d+(\.\d+)?$/.test(s)) return null;
    var n = parseFloat(s);
    if (isNaN(n) || n < 0 || n > RATING_MAX) return null;
    return n;
  }
  function ratingEl(score) {
    var wrap = document.createElement('span');
    wrap.className = 'fm-compare__rating';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', score + ' out of ' + RATING_MAX);
    var full = Math.floor(score);
    var frac = score - full;
    var half = frac >= 0.25 && frac < 0.75;
    var filled = frac >= 0.75 ? full + 1 : full;
    for (var i = 1; i <= RATING_MAX; i++) {
      var dot = document.createElement('span');
      var cls = 'fm-compare__rating-dot';
      if (i <= filled) cls += ' is-filled';
      else if (i === full + 1 && half) cls += ' is-half';
      dot.className = cls;
      wrap.appendChild(dot);
    }
    return wrap;
  }

  function buildTable() {
    var products = [];
    selected.forEach(function (p) { products.push(p); });
    if (!products.length) return;

    if (panelTitle) panelTitle.textContent = 'Compare ' + products.length + ' ' + NOUN;
    // Desktop: label + flexible product columns. Mobile (see CSS): fixed
    // widths so the table scrolls horizontally with a frozen label column.
    tableEl.style.setProperty('--fmcmp-cols', 'minmax(110px, 0.9fr) repeat(' + products.length + ', 1fr)');
    tableEl.style.setProperty('--fmcmp-cols-m', '128px repeat(' + products.length + ', 150px)');
    tableEl.innerHTML = '';

    var datas = products.map(function (p) { return cache.get(p.handle) || { fields: [] }; });

    // Union of field keys, in first-seen order, with their labels.
    var order = [];
    var labels = {};
    datas.forEach(function (d) {
      (d.fields || []).forEach(function (f) {
        if (!(f.key in labels)) { labels[f.key] = f.label; order.push(f.key); }
      });
    });
    // Per-product value lookup.
    var values = datas.map(function (d) {
      var m = {};
      (d.fields || []).forEach(function (f) { m[f.key] = f.value; });
      return m;
    });

    // Header row — blank label cell + product columns.
    tableEl.appendChild(cell('fm-compare__cell--head fm-compare__cell--label'));
    products.forEach(function (p) {
      var c = cell('fm-compare__cell--head');
      if (p.image) {
        var img = document.createElement('img');
        img.className = 'fm-compare__head-img';
        img.src = p.image; img.alt = p.title; img.loading = 'lazy';
        c.appendChild(img);
      }
      var name = document.createElement('span');
      name.className = 'fm-compare__head-name';
      name.textContent = p.title;
      c.appendChild(name);
      if (p.url) {
        var cta = document.createElement('a');
        cta.className = 'fm-compare__head-cta';
        cta.href = p.url;
        cta.textContent = 'View Options';
        c.appendChild(cta);
      }
      tableEl.appendChild(c);
    });

    // Striping resets at each group so the first row of a group is plain.
    var alt = false;
    function renderRow(label, cellValues, asRating) {
      var cls = alt ? ' fm-compare__cell--alt' : '';
      alt = !alt;
      tableEl.appendChild(cell('fm-compare__cell--label' + cls, label));
      cellValues.forEach(function (v) {
        var num = asRating ? parseRating(v) : null;
        if (num !== null) {
          var rc = cell('fm-compare__cell--value' + cls);
          rc.appendChild(ratingEl(num));
          tableEl.appendChild(rc);
        } else if (v != null && v !== '') {
          tableEl.appendChild(cell('fm-compare__cell--value' + cls, v));
        } else {
          var c = cell('fm-compare__cell--value' + cls);
          var dash = document.createElement('span');
          dash.className = 'fm-compare__empty';
          dash.textContent = '—';
          c.appendChild(dash);
          tableEl.appendChild(c);
        }
      });
    }
    function dataRow(key, label) {
      // Rating-keyed rows render numeric scores as circles (see ratingEl).
      var isRating = /rating/.test(String(key).toLowerCase());
      renderRow(label, values.map(function (m) { return m[key]; }), isRating);
    }
    function groupHeader(title) {
      var h = cell('fm-compare__group');
      // Text lives in an inner span so it can stay sticky-left while the
      // full-width grey bar spans the (horizontally-scrolling) table.
      var t = document.createElement('span');
      t.className = 'fm-compare__group-text';
      t.textContent = title;
      h.appendChild(t);
      tableEl.appendChild(h);
      alt = false;
    }
    function labelFor(key) { return LABELS[key] || labels[key]; }

    // 1. Headline rows, ungrouped, at the top: "Best for" (tagline) and
    //    "Delivery" (from each product's `delivery:` tag, resolved server-side).
    if (order.indexOf('tagline') !== -1) dataRow('tagline', labelFor('tagline'));
    if (datas.some(function (d) { return d.delivery; })) {
      renderRow('Delivery', datas.map(function (d) { return d.delivery; }));
    }

    // 2. Bucket remaining keys into groups (first matching group wins).
    var buckets = GROUPS.map(function () { return []; });
    order.forEach(function (key) {
      if (key === 'tagline') return;
      for (var gi = 0; gi < GROUPS.length; gi++) {
        var g = GROUPS[gi];
        if ((g.keys && g.keys.indexOf(key) !== -1) || g.test(key)) { buckets[gi].push(key); break; }
      }
    });
    GROUPS.forEach(function (g, gi) {
      if (!buckets[gi].length) return;            // skip empty groups
      groupHeader(g.title);
      buckets[gi].forEach(function (key) { dataRow(key, labelFor(key)); });
    });

    // 3. Pricing — always last, always present.
    groupHeader('Pricing');
    tableEl.appendChild(cell('fm-compare__cell--label fm-compare__cell--price', 'From Price'));
    datas.forEach(function (d) {
      var c = cell('fm-compare__cell--value fm-compare__cell--price');
      if (d.price) {
        var amt = document.createElement('span');
        amt.className = 'fm-compare__price-amount';
        amt.textContent = d.price;
        c.appendChild(amt);
        if (d.price_suffix) {
          var sfx = document.createElement('span');
          sfx.className = 'fm-compare__price-suffix';
          sfx.textContent = d.price_suffix;
          c.appendChild(sfx);
        }
      } else {
        c.textContent = '—';
      }
      tableEl.appendChild(c);
    });
  }

  /* ——— Keep checkboxes in step with state ——— */
  function syncCheckboxes() {
    var atMax = selected.size >= MAX;
    document.querySelectorAll('[data-fm-compare-product]').forEach(function (cb) {
      var on = selected.has(cb.getAttribute('data-fm-compare-product'));
      cb.checked = on;
      cb.disabled = !on && atMax;
    });
  }

  function pulseLimit() {
    openBtn.classList.remove('fm-compare__compare-btn--pulse');
    // force reflow so the animation can retrigger
    void openBtn.offsetWidth;
    openBtn.classList.add('fm-compare__compare-btn--pulse');
  }

  // The facet/sort/paginate flow swaps #ProductGridContainer's innerHTML,
  // recreating the checkboxes. Re-apply checked state after each swap.
  var grid = document.getElementById('ProductGridContainer');
  if (grid && 'MutationObserver' in window) {
    var raf = 0;
    new MutationObserver(function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; syncCheckboxes(); });
    }).observe(grid, { childList: true, subtree: true });
  }

  // Initial paint (handles pages where boxes are somehow pre-checked).
  document.querySelectorAll('[data-fm-compare-product]:checked').forEach(function (cb) {
    selected.set(cb.getAttribute('data-fm-compare-product'), cardData(cb));
  });
  render();
})();
